import {
  ApolloCache,
  DefaultContext,
  MutationFunctionOptions,
  MutationHookOptions,
  gql,
  useMutation,
} from '@apollo/client';
import { Failure, MessageCategory } from 'modules/dragAndDropEditor/types';
import { DataWithGraphQLTypeName, GRAPHQL_TYPENAMES } from 'modules/graphql/types';
import { useMemo } from 'react';
import { RequireAtLeastOneKey } from 'utils/tsHelpers';

export type ManageMessageCategoriesMutationFunction<
  Data,
  Variables,
  Context = DefaultContext,
  ErrorCache extends ApolloCache<any> = ApolloCache<any>,
> = (options?: MutationFunctionOptions<Data, Variables, Context, ErrorCache>) => void;

export type ManageMessageCategoryResult = Pick<MessageCategory, 'id' | 'name'>;
export type UpdateMessageCategoryArgsInput = ManageMessageCategoryResult[];
export type CreateMessageCategoryArgsInput = Pick<ManageMessageCategoryResult, 'name'>[];
export type DeleteMessageCategoryArgsInput = Pick<ManageMessageCategoryResult, 'id'>[];

export type ManageMessageCategoriesVariables = RequireAtLeastOneKey<{
  create?: CreateMessageCategoryArgsInput;
  update?: UpdateMessageCategoryArgsInput;
  delete?: DeleteMessageCategoryArgsInput;
}>;

export type BeeManageMessageCategoriesMutationFailure =
  | DataWithGraphQLTypeName<GRAPHQL_TYPENAMES.MESSAGE_CATEGORY_CREATION_FAILURE, Failure>
  | DataWithGraphQLTypeName<GRAPHQL_TYPENAMES.MESSAGE_CATEGORY_UPDATE_FAILURE, Failure>
  | DataWithGraphQLTypeName<GRAPHQL_TYPENAMES.MESSAGE_CATEGORY_DELETION_FAILURE, Failure>
  | DataWithGraphQLTypeName<GRAPHQL_TYPENAMES.MANAGE_MESSAGE_CATEGORIES_FAILURE, Failure>
  | DataWithGraphQLTypeName<
      GRAPHQL_TYPENAMES.MESSAGE_CATEGORY_MAXIMUM_COUNT_EXCEEDED_FAILURE,
      Failure
    >
  | DataWithGraphQLTypeName<GRAPHQL_TYPENAMES.MESSAGE_CATEGORY_NAME_ALREADY_EXISTS_FAILURE, Failure>
  | DataWithGraphQLTypeName<
      GRAPHQL_TYPENAMES.MESSAGE_CATEGORY_NAME_EMPTY_FAILURE,
      { __typename: string }
    >
  | DataWithGraphQLTypeName<
      GRAPHQL_TYPENAMES.MESSAGE_CATEGORY_NAME_TOO_LONG_FAILURE,
      { maximumLength: number }
    >
  | null;

export type ManageMessageCategoriesResponse = {
  manageMessageCategories: {
    created: ManageMessageCategoryResult[];
    updated: ManageMessageCategoryResult[];
    deleted: ManageMessageCategoryResult[];
    failure: BeeManageMessageCategoriesMutationFailure;
  };
};

export type ManageMessageCategoriesOptions = MutationHookOptions<
  ManageMessageCategoriesResponse,
  ManageMessageCategoriesVariables
>;

const manageMessageCategoriesMutation = gql`
  mutation ManageMessageCategories(
    $create: [CreateMessageCategoryArgsInput!]
    $update: [UpdateMessageCategoryArgsInput!]
    $delete: [DeleteMessageCategoryArgsInput!]
  ) {
    manageMessageCategories(create: $create, update: $update, delete: $delete) {
      created {
        id
        name
      }
      updated {
        id
        name
      }
      deleted {
        id
        name
      }
      failure {
        __typename
        ... on MessageCategoryUpdateFailure {
          errorMsg
        }
        ... on MessageCategoryDeletionFailure {
          errorMsg
        }
        ... on ManageMessageCategoriesFailure {
          errorMsg
        }
        ... on MessageCategoryCreationFailure {
          errorMsg
        }
        ... on MessageCategoryMaximumCountExceededFailure {
          errorMsg
        }
        ... on MessageCategoryNameAlreadyExistsFailure {
          errorMsg
        }
        ... on MessageCategoryNameEmptyFailure {
          __typename
        }
        ... on MessageCategoryNameTooLongFailure {
          maximumLength
        }
      }
    }
  }
`;

export const useManageMessageCategories = (options?: ManageMessageCategoriesOptions) => {
  const mutationOptions: ManageMessageCategoriesOptions = useMemo(
    () => ({
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'no-cache',
      ...options,
    }),
    [options],
  );

  const [manageMessageCategories, { data, loading: isLoading, reset }] = useMutation<
    ManageMessageCategoriesResponse,
    ManageMessageCategoriesVariables
  >(manageMessageCategoriesMutation, mutationOptions);

  return {
    failure: data?.manageMessageCategories.failure || null,
    isLoading,
    reset,
    manageMessageCategories,
  };
};
